/**
 * Super Admin — build a Looker template in the app
 *
 * /admin/looker-templates/new and /admin/looker-templates/:id/edit. A master
 * lays out tiles (chart type, fields, width), previews them on a workspace's
 * real data, and saves: a new template goes to the template folder; an edit
 * rebuilds the template in place, keeping each kept tile's Looker element so
 * every workspace's arrangement of it survives. "Save as copy" makes a new
 * template instead, leaving the one workspaces use untouched.
 *
 * Only templates made here can be edited here (looker_app_templates). One
 * made in Looker may hold what a spec cannot say, and saving over it would
 * lose that silently — the server refuses it with 409 as well.
 *
 * Not a security boundary: /api/looker-admin is behind authorizeMaster and
 * the spec is read under RLS that only a master passes.
 *
 * @module pages/admin/LookerTemplateBuilder
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { YDMPageHero } from '../../components/brand';
import { BuilderTileList } from '@/components/looker/admin/builder/BuilderTileList';
import { BuilderTileForm } from '@/components/looker/admin/builder/BuilderTileForm';
import { BuilderPreview, type PreviewTarget } from '@/components/looker/admin/builder/BuilderPreview';
import { EMPTY_SPEC, canAddTile, issuesOf, moveTile, newTile } from '@/components/looker/admin/builder/builderDraft';
import { listWorkspaces, type AdminWorkspace } from '@/services/adminService';
import {
  createLookerTemplate, fetchLookerBuilderFields, previewLookerTemplate, updateLookerTemplate,
  type LookerBuilderField, type LookerFetchResult,
} from '@/services/looker/lookerApi';
import { fetchAppTemplateSpec } from '@/services/looker/lookerTemplateAccess';
import type { TemplateSpec } from '@/services/looker/lookerTemplateSpec';
import type { LookerDashboardPayload } from '@/services/looker/lookerTypes';

const LIST_PATH = '/admin/looker-templates';
const SECTION = 'rounded-lg border border-neutral-200 bg-white p-4';
const INPUT = 'w-full rounded-md border border-neutral-300 px-2 py-1 text-sm';
const BUTTON =
  'inline-flex h-8 items-center rounded-md border border-neutral-300 bg-white px-3 text-sm font-medium text-ink-900 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY =
  'inline-flex h-8 items-center rounded-md bg-primary-600 px-3 text-sm font-medium text-ink-950 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50';

const AdminLookerTemplateBuilder: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: templateId } = useParams<{ id?: string }>();
  const editing = templateId !== undefined;

  const [spec, setSpec] = useState<TemplateSpec>(EMPTY_SPEC);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [fields, setFields] = useState<LookerBuilderField[]>([]);
  const [workspaces, setWorkspaces] = useState<AdminWorkspace[]>([]);
  const [target, setTarget] = useState<PreviewTarget>('all');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<LookerDashboardPayload | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  /** The server's own words when it gave any (a 400 names the field); otherwise what the status means. */
  const failure = (r: Extract<LookerFetchResult<unknown>, { ok: false }>): string => {
    if (r.status === 400 && r.error) return r.error;
    if (r.status === 401 || r.status === 403) return t('looker.admin.errors.forbidden');
    if (r.status === 404) return t('looker.admin.errors.notFound');
    if (r.status === 409) return t('looker.builder.notAppMade');
    if (r.status === 429) return t('looker.admin.errors.rateLimited');
    if (r.status === 503) return t('looker.admin.errors.notConnected');
    if (r.status === 502 || r.status === 0) return t('looker.admin.errors.unavailable');
    return t('looker.admin.errors.saveFailed');
  };

  useEffect(() => {
    let live = true;
    void (async () => {
      const [f, ws, stored] = await Promise.all([
        fetchLookerBuilderFields(),
        listWorkspaces(),
        templateId ? fetchAppTemplateSpec(templateId) : Promise.resolve(null),
      ]);
      if (!live) return;
      if (ws.success) setWorkspaces(ws.data ?? []);
      if (!f.ok) setLoadError(failure(f));
      else setFields(f.data.fields);
      if (templateId) {
        if (stored === 'error') setLoadError(t('looker.builder.loadFailed'));
        else if (stored === null) setLoadError(t('looker.builder.notAppMade'));
        else {
          setSpec(stored);
          setSelectedKey(stored.tiles[0]?.key ?? null);
        }
      }
      setLoading(false);
    })();
    return () => {
      live = false;
    };
    // Loaded once per template; `failure` and `t` only format the message.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const issues = useMemo(() => issuesOf(spec), [spec]);
  const invalidTiles = useMemo(() => new Set(Object.keys(issues.tiles).map(Number)), [issues]);
  const selectedIndex = spec.tiles.findIndex((tile) => tile.key === selectedKey);
  const selected = selectedIndex >= 0 ? spec.tiles[selectedIndex] : null;

  const setTiles = (tiles: TemplateSpec['tiles']) => setSpec({ ...spec, tiles });
  const addTile = () => {
    const tile = newTile(spec.tiles);
    setTiles([...spec.tiles, tile]);
    setSelectedKey(tile.key);
  };
  const removeTile = (index: number) => {
    const tiles = spec.tiles.filter((_, i) => i !== index);
    setTiles(tiles);
    if (spec.tiles[index]?.key === selectedKey) setSelectedKey(tiles[Math.min(index, tiles.length - 1)]?.key ?? null);
  };

  const runPreview = async () => {
    setPreviewing(true);
    setPreviewError(null);
    const r = await previewLookerTemplate(spec, target === 'all' ? { kind: 'all' } : { kind: 'workspace', workspaceId: target });
    if (r.ok) setPreview(r.data);
    else setPreviewError(failure(r));
    setPreviewing(false);
  };

  const save = async (asCopy: boolean) => {
    setSaving(true);
    setSaveError(null);
    const r = asCopy
      ? await createLookerTemplate({ ...spec, title: `${spec.title.trim()} (copy)`.slice(0, 100) })
      : templateId
        ? await updateLookerTemplate(templateId, spec)
        : await createLookerTemplate(spec);
    setSaving(false);
    if (r.ok) navigate(LIST_PATH);
    else setSaveError(failure(r));
  };

  const blocked = loadError !== null;

  return (
    <div className="space-y-4 animate-fade-in">
      <YDMPageHero
        eyebrow="Super Admin"
        headline={editing ? t('looker.builder.editHeadline') : t('looker.builder.newHeadline')}
        subtitle={t('looker.builder.subtitle')}
      />

      {loadError && <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{loadError}</p>}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-neutral-800" role="status">
          <Loader2 className="h-6 w-6 animate-spin text-ink-900" aria-hidden />
          {t('looker.builder.loading')}
        </div>
      ) : (
        <>
          <section className={`${SECTION} grid grid-cols-1 gap-3 md:grid-cols-2`}>
            <div>
              <label className="block text-sm font-medium text-ink-900" htmlFor="builder-title">{t('looker.builder.titleLabel')}</label>
              <input id="builder-title" className={INPUT} value={spec.title} maxLength={100} disabled={blocked}
                onChange={(e) => setSpec({ ...spec, title: e.target.value })} />
              {issues.template.title && <p className="mt-1 text-xs text-red-700">{issues.template.title}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-900" htmlFor="builder-description">{t('looker.builder.descriptionLabel')}</label>
              <textarea id="builder-description" className={INPUT} rows={2} value={spec.description} maxLength={500} disabled={blocked}
                onChange={(e) => setSpec({ ...spec, description: e.target.value })} />
            </div>
          </section>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[16rem_minmax(0,1fr)_minmax(0,1.4fr)]">
            <section className={SECTION} aria-label={t('looker.builder.tiles')}>
              <BuilderTileList
                tiles={spec.tiles}
                selectedKey={selectedKey}
                invalid={invalidTiles}
                canAdd={!blocked && canAddTile(spec)}
                onSelect={setSelectedKey}
                onAdd={addTile}
                onRemove={removeTile}
                onMove={(from, to) => setTiles(moveTile(spec.tiles, from, to))}
              />
              {issues.template.tiles && <p className="mt-2 text-xs text-red-700">{issues.template.tiles}</p>}
            </section>

            <section className={SECTION} aria-label={t('looker.builder.tileForm')}>
              {selected ? (
                <BuilderTileForm
                  key={selected.key}
                  tile={selected}
                  fields={fields}
                  issues={issues.tiles[selectedIndex] ?? {}}
                  onChange={(tile) => setTiles(spec.tiles.map((x, i) => (i === selectedIndex ? tile : x)))}
                />
              ) : (
                <p className="text-sm text-neutral-800">{t('looker.builder.selectTile')}</p>
              )}
            </section>

            <div className={SECTION}>
              <BuilderPreview
                workspaces={workspaces}
                target={target}
                onTarget={setTarget}
                onPreview={() => void runPreview()}
                disabled={blocked || !issues.valid}
                busy={previewing}
                payload={preview}
                error={previewError}
              />
            </div>
          </div>

          {saveError && <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{saveError}</p>}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className={BUTTON} onClick={() => navigate(LIST_PATH)}>{t('looker.builder.cancel')}</button>
            {editing && (
              <button type="button" className={BUTTON} disabled={blocked || saving || !issues.valid} onClick={() => void save(true)}>
                {t('looker.builder.saveAsCopy')}
              </button>
            )}
            <button type="button" className={PRIMARY} disabled={blocked || saving || !issues.valid} onClick={() => void save(false)}>
              {saving ? t('looker.builder.saving') : t('looker.builder.save')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminLookerTemplateBuilder;
